import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../../library/elements/d-action';
import './monthly-hours-list';
import type { DayEntry } from './day-entry';
import type { StaffingCalendarDataDay } from '../../../pages/staffing-page/staffing-calendar-data-day';
import { minutesToTime } from '../../../utilities/text';
import { Task } from '@lit/task';
import { postTripletex } from '../../../store/backend';
import type { MonthlyHoursSummaryData } from '../summary-view';
import { t } from 'src/i18n';

/**
 * Monthly hours summary component
 * Shows timesheet data for employees with Tripletex integration
 */
@customElement('d-monthly-hours-summary')
export class DMonthlyHoursSummary extends LitElement {
  @property({ type: Object })
  data!: MonthlyHoursSummaryData;

  @property({ type: String })
  parentHref = '';

  private timesheetTask = new Task(this, {
    task: async ([e, ym]) => {
      if (e === undefined) {
        return undefined;
      }
      if (ym === undefined) {
        return undefined;
      }
      return postTripletex(e, ym);
    },
    args: () => [this.data.selectedEmployee, this.data.selectedYearMonth],
  });

  render() {
    const currentEmployee = this.data.selectedEmployee ?? '';
    const currentYear = this.data.selectedYear?.toString() ?? '';
    const currentMonth = this.data.selectedYearMonth?.split('-')[1] ?? '';

    return html`
      <div style="padding: 20px;">
        <h3>${t('summaries.monthlyHours.selectEmployee')}</h3>
        <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px;">
          ${this.data.availableEmployees.map((e) => {
            const isSelected = e.value === currentEmployee;
            return html`<d-action
              .href="${this.parentHref}/summaries/monthly-hours:${e.value}:${currentYear}:${currentMonth}"
              style="${isSelected ? 'background: var(--themeColor); color: white;' : ''}"
              >${e.text}</d-action
            >`;
          })}
        </div>

        <h3>${t('summaries.monthlyHours.selectYear')}</h3>
        <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px;">
          ${this.data.availableYears.map((y) => {
            const isSelected = y.value.toString() === currentYear;
            return html`<d-action
              .href="${this.parentHref}/summaries/monthly-hours:${currentEmployee}:${y.value}:${currentMonth}"
              style="${isSelected ? 'background: var(--themeColor); color: white;' : ''}"
              >${y.text}</d-action
            >`;
          })}
        </div>

        <h3>${t('summaries.monthlyHours.selectMonth')}</h3>
        <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px;">
          ${this.data.availableMonths.map((m) => {
            const isSelected = m.value === currentMonth;
            return html`<d-action
              .href="${this.parentHref}/summaries/monthly-hours:${currentEmployee}:${currentYear}:${m.value}"
              style="${isSelected ? 'background: var(--themeColor); color: white;' : ''}"
              >${m.text}</d-action
            >`;
          })}
        </div>

        ${this.data.selectedEmployeeMonth !== undefined
          ? html`
              <h3>${t('summaries.monthlyHours.timesheetFor', { name: this.data.selectedEmployeeMonth.name })}</h3>
              <monthly-hours-list .days=${this.toDayEntries(this.data.selectedEmployeeMonth.days)}></monthly-hours-list>
            `
          : html`<p style="color: #666; font-style: italic;">${t('summaries.monthlyHours.selectPrompt')}</p>`}
      </div>
    `;
  }

  private lookupHours(
    values: { date: string; hours: number; projectDisplayName: string; activityDisplayName: string }[],
    date: string,
  ): number {
    const f = values.filter((x) => x.date === date);

    return f.map((x) => x.hours).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  }

  private toDayEntries(days: StaffingCalendarDataDay[]): DayEntry[] {
    const paySystemHoursValues = this.timesheetTask.value ?? [];

    return days.map((d) => {
      return {
        date: d.date,
        workPeriods: d.workPeriods.map((w) => {
          let extraHours = 0;
          d.plusTimePeriods.forEach((p) => {
            extraHours += (p.endMinutes - p.startMinutes) / 60;
          });
          d.leavePeriods.forEach((p) => {
            if (p.type === 'timeOff') {
              extraHours -= (p.endMinutes - p.startMinutes) / 60;
            }
          });
          return {
            from: minutesToTime(w.startMinutes),
            to: minutesToTime(w.endMinutes),
            salariedHours: d.workSchedule.workHours,
            extraHours: extraHours,
            paySystemHours: this.lookupHours(paySystemHoursValues, d.date),
          };
        }),
      };
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-monthly-hours-summary': DMonthlyHoursSummary;
  }
}
